/**
 * ThemeSettingsPage - Strona ustawień wyglądu (Global Skin)
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';

const CATEGORIES = {
  czcionka: { label: 'Czcionka', icon: 'Aa' },
  kolory: { label: 'Kolory', icon: '🎨' },
  uklad: { label: 'Układ', icon: '📐' },
  inne: { label: 'Inne', icon: '⚙️' }
};

const LABELS = {
  font_family: 'Rodzaj czcionki',
  font_size_base: 'Rozmiar tekstu',
  font_size_headings: 'Rozmiar nagłówków',
  theme_mode: 'Tryb kolorystyczny',
  primary_color: 'Kolor akcentu',
  sidebar_color: 'Kolor paska bocznego',
  ui_density: 'Gęstość interfejsu',
  border_radius: 'Zaokrąglenie rogów',
  button_size: 'Rozmiar przycisków',
  table_density: 'Gęstość tabel',
  animations_enabled: 'Animacje',
  sidebar_collapsed: 'Pasek boczny zwinięty'
};

const VALUE_LABELS = {
  theme_mode: { light: 'Jasny', dark: 'Ciemny', auto: 'Automatyczny' },
  font_size_headings: { small: 'Małe', normal: 'Normalne', large: 'Duże' },
  ui_density: { compact: 'Kompaktowy', normal: 'Normalny', comfortable: 'Komfortowy' },
  border_radius: { none: 'Brak', small: 'Małe', medium: 'Średnie', large: 'Duże', full: 'Pełne' },
  button_size: { small: 'Małe', medium: 'Średnie', large: 'Duże' },
  table_density: { compact: 'Kompaktowe', normal: 'Normalne', comfortable: 'Komfortowe' }
};

export default function ThemeSettingsPage() {
  const { settings: currentSettings, updateSettings, resetSettings } = useTheme();
  const [groupedSettings, setGroupedSettings] = useState({});
  const [localSettings, setLocalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeCategory, setActiveCategory] = useState('czcionka');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/theme-settings');
      if (response.data?.data?.grouped) {
        setGroupedSettings(response.data.data.grouped);
        setLocalSettings(response.data.data.flat || {});
      }
    } catch (error) {
      console.error('Błąd pobierania ustawień:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const success = await updateSettings(localSettings);
    setSaving(false);
    if (success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Czy na pewno chcesz przywrócić domyślne ustawienia wyglądu?')) {
      await resetSettings();
      fetchSettings();
    }
  };

  const renderSettingInput = (setting) => {
    const { klucz, wartosc, typ, opcje } = setting;
    const value = localSettings[klucz] ?? wartosc;

    if (typ === 'color') {
      return (
        <div className="color-picker-wrapper">
          <input
            type="color"
            value={value}
            onChange={(e) => handleChange(klucz, e.target.value)}
            className="color-picker"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(klucz, e.target.value)}
            className="color-text"
            pattern="^#[0-9A-Fa-f]{6}$"
          />
        </div>
      );
    }

    if (typ === 'boolean') {
      return (
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => handleChange(klucz, e.target.checked ? 'true' : 'false')}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">{value === 'true' ? 'Włączone' : 'Wyłączone'}</span>
        </label>
      );
    }

    if (typ === 'select' && opcje) {
      const options = Array.isArray(opcje) ? opcje : [];
      return (
        <div className="select-buttons">
          {options.map(opt => (
            <button
              key={opt}
              className={`select-btn ${value === opt ? 'active' : ''}`}
              onClick={() => handleChange(klucz, opt)}
            >
              {VALUE_LABELS[klucz]?.[opt] || opt}
            </button>
          ))}
        </div>
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(klucz, e.target.value)}
        className="form-input"
      />
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Ładowanie ustawień...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <span style={{ fontSize: '1.75rem' }}>🎨</span>
          <div>
            <h1>Ustawienia wyglądu</h1>
            <p className="subtitle">Dostosuj wygląd panelu administracyjnego</p>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleReset}>
            🔄 Przywróć domyślne
          </button>
          <button
            className={`btn btn-primary ${saved ? 'btn-success' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>Zapisywanie...</>
            ) : saved ? (
              <>✓ Zapisano</>
            ) : (
              <>💾 Zapisz zmiany</>
            )}
          </button>
        </div>
      </div>

      <div className="theme-settings-layout">
        {/* Kategorie - sidebar */}
        <div className="theme-categories">
          {Object.entries(CATEGORIES).map(([key, { label, icon }]) => (
            <button
              key={key}
              className={`category-btn ${activeCategory === key ? 'active' : ''}`}
              onClick={() => setActiveCategory(key)}
            >
              <span>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Ustawienia */}
        <div className="theme-settings-panel">
          <div className="settings-category">
            <h2>
              <span>{CATEGORIES[activeCategory].icon}</span>
              {CATEGORIES[activeCategory].label}
            </h2>

            <div className="settings-list">
              {(groupedSettings[activeCategory] || []).map(setting => (
                <div key={setting.klucz} className="setting-item">
                  <div className="setting-label">
                    <span className="label-text">{LABELS[setting.klucz] || setting.klucz}</span>
                    {setting.opis && <span className="label-desc">{setting.opis}</span>}
                  </div>
                  <div className="setting-control">
                    {renderSettingInput(setting)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Podgląd */}
        <div className="theme-preview">
          <h3>Podgląd</h3>
          <div className="preview-card">
            <div className="preview-header">Przykładowa karta</div>
            <div className="preview-content">
              <p>To jest przykładowy tekst w podstawowym rozmiarze czcionki.</p>
              <div className="preview-buttons">
                <button className="btn btn-primary btn-sm">Przycisk główny</button>
                <button className="btn btn-secondary btn-sm">Przycisk drugi</button>
              </div>
            </div>
            <div className="preview-table">
              <table>
                <thead>
                  <tr><th>Kolumna 1</th><th>Kolumna 2</th></tr>
                </thead>
                <tbody>
                  <tr><td>Wartość A</td><td>123.45 zł</td></tr>
                  <tr><td>Wartość B</td><td>67.89 zł</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .page-container {
          padding: 1.5rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .page-title h1 {
          margin: 0;
          font-size: 1.5rem;
        }

        .page-title .subtitle {
          color: var(--text-muted);
          font-size: 0.875rem;
          margin: 0;
        }

        .page-actions {
          display: flex;
          gap: 0.75rem;
        }

        .theme-settings-layout {
          display: grid;
          grid-template-columns: 200px 1fr 300px;
          gap: 1.5rem;
        }

        @media (max-width: 1200px) {
          .theme-settings-layout {
            grid-template-columns: 180px 1fr;
          }
          .theme-preview {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .theme-settings-layout {
            grid-template-columns: 1fr;
          }
          .theme-categories {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
        }

        .theme-categories {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .category-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border: none;
          background: transparent;
          border-radius: var(--border-radius);
          cursor: pointer;
          text-align: left;
          transition: all var(--transition-duration);
          color: var(--text-muted);
        }

        .category-btn:hover {
          background: var(--bg);
          color: #334155;
        }

        .category-btn.active {
          background: var(--color-primary-light);
          color: var(--color-primary);
          font-weight: 500;
        }

        .theme-settings-panel {
          background: white;
          border-radius: var(--border-radius);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          padding: 1.5rem;
        }

        .settings-category h2 {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.125rem;
          margin: 0 0 1.5rem 0;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border);
        }

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .setting-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg);
          border-radius: var(--border-radius);
        }

        .setting-label {
          flex: 1;
        }

        .label-text {
          display: block;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }

        .label-desc {
          display: block;
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .setting-control {
          flex-shrink: 0;
        }

        .select-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .select-btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--border);
          background: white;
          border-radius: var(--border-radius);
          cursor: pointer;
          transition: all var(--transition-duration);
          font-size: 0.875rem;
        }

        .select-btn:hover {
          border-color: var(--color-primary);
        }

        .select-btn.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }

        .color-picker-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .color-picker {
          width: 50px;
          height: 38px;
          padding: 2px;
          border: 1px solid var(--border);
          border-radius: var(--border-radius);
          cursor: pointer;
        }

        .color-text {
          width: 90px;
          padding: 0.5rem;
          border: 1px solid var(--border);
          border-radius: var(--border-radius);
          font-family: monospace;
        }

        .toggle-switch {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
        }

        .toggle-switch input {
          display: none;
        }

        .toggle-slider {
          width: 48px;
          height: 26px;
          background: var(--border);
          border-radius: 13px;
          position: relative;
          transition: background var(--transition-duration);
        }

        .toggle-slider::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          transition: transform var(--transition-duration);
        }

        .toggle-switch input:checked + .toggle-slider {
          background: var(--color-primary);
        }

        .toggle-switch input:checked + .toggle-slider::after {
          transform: translateX(22px);
        }

        .toggle-label {
          font-size: 0.875rem;
          color: var(--text-muted);
        }

        .theme-preview {
          background: white;
          border-radius: var(--border-radius);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          padding: 1.5rem;
        }

        .theme-preview h3 {
          margin: 0 0 1rem 0;
          font-size: 1rem;
          color: var(--text-muted);
        }

        .preview-card {
          border: 1px solid var(--border);
          border-radius: var(--border-radius);
          overflow: hidden;
        }

        .preview-header {
          background: var(--color-primary);
          color: white;
          padding: var(--ui-padding);
          font-weight: 500;
        }

        .preview-content {
          padding: var(--ui-spacing);
        }

        .preview-content p {
          margin: 0 0 1rem 0;
          font-size: var(--font-size-base);
        }

        .preview-buttons {
          display: flex;
          gap: var(--ui-gap);
        }

        .preview-table {
          border-top: 1px solid var(--border);
        }

        .preview-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .preview-table th,
        .preview-table td {
          padding: var(--table-cell-padding);
          text-align: left;
          font-size: 0.875rem;
        }

        .preview-table th {
          background: var(--bg);
          font-weight: 500;
        }

        .preview-table tr:not(:last-child) td {
          border-bottom: 1px solid var(--border);
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          min-height: 32px;
          font-size: 0.8rem;
        }

        .btn-success {
          background: var(--success) !important;
        }
      `}</style>
    </div>
  );
}
